import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { trackInternalEvent } from '@rio/tracking'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { SuggestionSelectedEventData } from 'react-autosuggest'
import { useNavigate } from 'react-router-dom'
import debounce from 'lodash/debounce'
import { useI18n } from 'builder/hooks/useI18n'
import * as SimpleForm from 'builder/components/SimpleForm'
import { TextField } from 'builder/components/TextField'
import FieldSpinnerLoader from 'builder/views/ResumeDistributionView/components/FormLayout/FieldSpinnerLoader'
import { FullTick } from 'builder/views/ResumeDistributionView/assets/svg/FullTick'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  selectors as resumeDistributionSelectors,
  actions as resumeDistributionActions,
  AutoSuggestItem,
  QuestionnaireForm,
  CandidatePayload,
  ResumeDetails,
} from 'builder/modules/resumeDistribution'
import { createNewLocationSuggestionsApiFetcher } from 'builder/components/FindJob/utils/createNewSuggestionsApiFetcher'
import { AsyncAutosuggest } from 'builder/components/AsyncAutosuggest'
import { validateEmail } from 'builder/views/ResumeDistributionView/components/PersonalDetailsForm/utils'
import Select from 'builder/components/Select'
import {
  Item,
  TransformedItem,
  convertFormDataToObject,
  fetchIndustriesSuggestions,
  fetchStatesSuggestions,
} from 'builder/views/ResumeDistributionView/components/JobPreferencesForm/utils'
import Toggle from 'builder/components/Toggle'
import {
  PERIOD,
  CURRENCY_SIGN,
  CURRENCY,
  COUNTRIES,
} from 'builder/views/ResumeDistributionView/components/JobPreferencesForm/constants'
import { fileToBase64, stringifyFormData } from 'builder/views/ResumeDistributionView/utils'
import { goToBillingPlanPage } from 'builder/utils/goToBillingPlanPage'
import { selectors as userSelectors } from 'builder/modules/user'
import { actions as uiActions, SnackbarTypes } from 'builder/modules/ui'
import ErrorLogger from 'builder/services/ErrorLogger'
import { DocumentTypes, FetchStatuses } from 'builder/modules/constants'
import useWebsiteHost from 'builder/components/CIO-Dasboard-2.0/hooks/useWebsiteHost'
import { baseClient } from 'builder/modules/apiClient'
import { ButtonV2 } from 'builder/components/Button'
import { useEmailCanBeUsed } from 'builder/views/ResumeDistributionView/components/PersonalDetailsForm/hooks/useEmailIsInUse'
import { ResumeFilePreview } from 'builder/views/ResumeDistributionView/components/PersonalDetailsForm/ResumePreview/ResumeFilePreview'
import { ResumeBuilderPreview } from 'builder/views/ResumeDistributionView/components/PersonalDetailsForm/ResumePreview/ResumeBuilderPreview'
import { useTrDocParse } from 'builder/views/ResumeDistributionView/components/PersonalDetailsForm/hooks/useTrDocsParse'
import { BriefExpertDoc, BriefResume, selectors as panelSelectors } from 'builder/modules/panel'
import { FieldNote } from 'builder/components/FieldNote'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { ResumeDistributionLayout } from '../ResumeDistributionLayout/ResumeDistributionLayout'
import { AvailableCountriesBanner } from '../AvailableCountriesBanner/AvailableCountriesBanner'
import { RecruiterCountBanner } from '../RecruiterCountBanner/RecruiterCountBanner'
import { ReactSelectOption } from '../../types'
import {
  Container,
  TitleContainer,
  Title,
  FormContainer,
  SpinnerRightBar,
  LabelSpinner,
  PreFillingText,
  PreFilledText,
  EmailErrorContainer,
  ContactSupport,
  ToggleLabel,
  TextFieldAmount,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  TextFieldHint,
  ActionContainer,
  StyledSelect,
  FormGrid,
  FormGridItem,
  HeaderContainer,
} from './styles'

const TRANSLATION = 'builder.resume_distribution.version_3.questionnaire'

export const Questionnaire = () => {
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isPhone } = useMediaQueries()
  const { getHost } = useWebsiteHost()
  const upgradeUrl = useTypedSelector(userSelectors.getUpgradeUrl)
  const formState = useTypedSelector(resumeDistributionSelectors.questionnaireFormFields)
  const userAccountData = useTypedSelector(userSelectors.userData)
  const candidate = useTypedSelector(resumeDistributionSelectors.candidate)
  const candidatePostStatus = useTypedSelector(resumeDistributionSelectors.candidatePostStatus)
  const candidatePatchStatus = useTypedSelector(resumeDistributionSelectors.candidatePatchStatus)
  const candidateResumePostStatus = useTypedSelector(
    resumeDistributionSelectors.candidateResumePostStatus,
  )
  const candidateResumeFailed = useTypedSelector(resumeDistributionSelectors.candidateResumeFailed)
  const serverFailed = useTypedSelector(resumeDistributionSelectors.serverFailed)
  const isSubscriptionActive = useTypedSelector(resumeDistributionSelectors.isSubscriptionActive)
  const isEdit = useTypedSelector(resumeDistributionSelectors.isEdit)
  const isParsing = useTypedSelector<boolean>(resumeDistributionSelectors.isParsing)
  const {
    isLoading: isEmailValidationPending,
    mutateAsync: isEmailInUse,
    data: emailCanBeUsed,
  } = useEmailCanBeUsed()
  const resume = useTypedSelector(resumeDistributionSelectors.selectedResumeDetails)
  const completeResume = useTypedSelector(resumeDistributionSelectors.formResumeFetchDetails)

  const documentsList = useTypedSelector(panelSelectors.documents)
  const { mutateAsync, isLoading } = useTrDocParse()
  const form = useForm({
    mode: 'onChange',
    defaultValues: formState,
  })
  const hasTrackedRef = useRef(false)

  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = form

  const [industries, setIndustries] = useState<ReactSelectOption[]>([])
  const [location, setLocation] = useState<{ country?: string; state?: string }>({
    country: watch('country') || undefined,
    state: watch('state')?.text,
  })

  const required = i18n.t(`${TRANSLATION}.required_field`)

  const selectedIndustry = industries.find(item => item.value === watch('industry')) || null

  const setFieldValue = useCallback(
    (field: string, value?: number | string | AutoSuggestItem) => {
      dispatch(resumeDistributionActions.setJobPreferencesForm({ [field]: value }))
    },
    [dispatch],
  )

  const storeToLocalStorage = async (data: FormData) => {
    const stringifiedData = stringifyFormData(data)
    localStorage.setItem('resume-distribution', stringifiedData)

    const file = data.get('resume_file') as File
    if (file) {
      const base64 = await fileToBase64(file)
      localStorage.setItem('resume-distribution-file', base64)
      localStorage.setItem('resume-distribution-file-name', file.name)
    }
  }

  const redirectOnPayments = () => {
    goToBillingPlanPage({
      upgradeURL: upgradeUrl,
      referrerPage: 'resumeDistribution',
      onSuccessRedirect: '/app/resume-distribution?submit_resume=true',
    })
  }

  const fetchIndustries = async (category?: 'top' | 'all') => {
    const industriesResponse = await fetchIndustriesSuggestions(category)

    const reactSelectOptions: ReactSelectOption[] = industriesResponse.map((item: Item) => ({
      value: item.id,
      label: item.name,
      key: item.id,
    }))
    setIndustries(reactSelectOptions)
  }

  const onChangeLocation = (field: string, value: string) => {
    setLocation({ ...location, [field]: value })
  }

  const onSelectField = useCallback(
    (field: keyof QuestionnaireForm, value: string | number) => {
      if (field === 'salaryAmount') {
        trackInternalEvent('enter_salary', {
          label: 'rd_form',
          salary: value,
          website_host: getHost,
        })
      }
      setValue(field, value)
      setFieldValue(field, value)
    },
    [getHost, setFieldValue, setValue],
  )

  const debouncedTrackFieldChange = useMemo(
    () =>
      debounce((field: keyof QuestionnaireForm, value: string | number) => {
        onSelectField(field, value)
      }, 1000),
    [onSelectField],
  )

  useEffect(() => {
    return () => debouncedTrackFieldChange.cancel()
  }, [debouncedTrackFieldChange])

  const getError = (field: keyof QuestionnaireForm) => {
    if (errors[field]) {
      const err = errors[field] as { message: string }
      return err?.message
    }
  }

  const onSuggestionSelected =
    (field: 'country' | 'state') =>
    (_: React.FormEvent<unknown>, { suggestion }: SuggestionSelectedEventData<TransformedItem>) => {
      if (field === 'state') {
        trackInternalEvent('select_job_state', {
          label: 'rd_form',
          website_host: getHost,
        })
      }
      setValue(field, suggestion, { shouldValidate: true })
      onChangeLocation(field, suggestion.text)
      setFieldValue(field, suggestion)
    }

  useEffect(() => {
    fetchIndustries('all')
  }, [])

  const editCandidate = async (bodyFormData: FormData) => {
    const payload = convertFormDataToObject<CandidatePayload>(bodyFormData)

    if (formState?.email === candidate?.email) {
      payload.email = undefined
    }

    try {
      if (!isSubscriptionActive) {
        await baseClient.post('resume-distribution/candidates/subscriptions', {})
      }

      if (resume) {
        dispatch(resumeDistributionActions.postCandidateResume(resume))
      }

      dispatch(resumeDistributionActions.patchCandidate(payload))
    } catch (err) {
      ErrorLogger.log(err)
    }
  }

  const setEmailInUseErrorMessage = () => {
    const message = i18n.t(`builder.resume_distribution.landing.form_personal_detail.email_in_use`)
    form.setError('email', { message })
  }

  const handleSubmit = async () => {
    const formValues = form.getValues()

    if (formValues?.email && candidate?.email !== formValues?.email) {
      const canBeUsed = await isEmailInUse(formValues?.email)
      if (!canBeUsed) {
        return setEmailInUseErrorMessage()
      }
    }

    const bodyFormData = new FormData()
    bodyFormData.append('first_name', formValues?.firstName || '')
    bodyFormData.append('last_name', formValues?.lastName || '')
    bodyFormData.append('job_title', formValues?.jobTitle || '')
    bodyFormData.append('email', formValues?.email || '')
    bodyFormData.append('full_address', formValues?.fullAddress || '')
    bodyFormData.append('preferences[country_id]', formValues.country || '')
    bodyFormData.append('preferences[state_id]', formValues.state?.id || '')
    bodyFormData.append('preferences[industry_id]', formValues.industry?.toString() || '')
    bodyFormData.append('preferences[remote]', formValues.remote === 'true' ? 'yes' : 'no')
    bodyFormData.append('preferences[desired_salary][currency]', formValues.salaryCurrency)
    bodyFormData.append('preferences[desired_salary][amount]', formValues.salaryAmount)
    bodyFormData.append('preferences[desired_salary][per]', formValues.salaryPeriod)

    if (resume) {
      if (resume instanceof Blob) {
        bodyFormData.append('resume_file', resume)
      } else if (resume.type === 'tr_document') {
        bodyFormData.append('document_id', resume.id.toString())
      } else {
        bodyFormData.append('resume_id', resume.id.toString())
      }
    }

    trackInternalEvent('rd_start_distributing', {
      website_host: getHost,
    })

    if (!userAccountData?.hasPremiumFeatures) {
      await storeToLocalStorage(bodyFormData)
      redirectOnPayments()
      return
    }

    if (isEdit) {
      editCandidate(bodyFormData)
    } else {
      dispatch(resumeDistributionActions.postCandidate(bodyFormData))
    }
  }

  const openSnackBar = useCallback(() => {
    setTimeout(() => {
      dispatch(
        uiActions.setSnackBarOpen({
          status: true,
          type: SnackbarTypes.success,
          text: i18n.t(`${TRANSLATION}.edit_info`),
        }),
      )
    }, 1000)

    setTimeout(() => {
      dispatch(uiActions.closeSnackBar())
    }, 5000)
  }, [dispatch, i18n])

  const checkSubmit = useCallback(() => {
    if (
      candidatePostStatus === FetchStatuses.loaded ||
      candidatePatchStatus === FetchStatuses.loaded ||
      candidateResumePostStatus === FetchStatuses.loaded
    ) {
      if (isEdit) {
        openSnackBar()
      }
      dispatch(resumeDistributionActions.setPostCandidateStatus(FetchStatuses.notAsked))
      dispatch(resumeDistributionActions.setPatchCandidateStatus(FetchStatuses.notAsked))
      dispatch(resumeDistributionActions.setPostCandidateResumeStatus(FetchStatuses.notAsked))
      dispatch(resumeDistributionActions.setIsEdit(false))
      navigate('/resume-distribution/dashboard')
    }

    if (candidateResumeFailed) {
      navigate('/resume-distribution/invalid-document')
      dispatch(resumeDistributionActions.setCandidateResumeFailed(false))
      return
    }

    if (
      candidatePostStatus === FetchStatuses.failed ||
      candidatePatchStatus === FetchStatuses.failed ||
      candidateResumePostStatus === FetchStatuses.failed
    ) {
      if (serverFailed) {
        navigate('/resume-distribution/server-error')
        dispatch(resumeDistributionActions.setServerFailed(false))
      }
    }
  }, [
    candidatePatchStatus,
    candidatePostStatus,
    candidateResumePostStatus,
    candidateResumeFailed,
    serverFailed,
    dispatch,
    isEdit,
    navigate,
    openSnackBar,
  ])

  useEffect(() => {
    checkSubmit()
  }, [candidatePatchStatus, candidatePostStatus, candidateResumePostStatus, checkSubmit])

  const parseTrDocuments = useCallback(
    async (documentId: number) => {
      try {
        const data = await mutateAsync(documentId)
        const payload = {
          ...data,
          jobTitle: data?.workExperiences?.[0]?.title,
          address: data?.address || '',
        }
        dispatch(resumeDistributionActions.setPopulationFormDetailsForResume(payload))
      } catch (error) {
        dispatch(resumeDistributionActions.setPopulationFormDetailsForResume(null))
      }
    },
    [dispatch, mutateAsync],
  )

  const setSelectedResumeDetails = useCallback(
    (resumeDetails: ResumeDetails) => {
      if (resumeDetails) {
        dispatch(resumeDistributionActions.setResumeDetails(resumeDetails))
      }
    },
    [dispatch],
  )

  const resetFormState = () => {
    dispatch(
      resumeDistributionActions.setPersonalDetailsForm({
        firstName: '',
        lastName: '',
        email: '',
        jobTitle: '',
        fullAddress: '',
        edit_file_name: '',
      }),
    )
  }

  function formatFullAddress(
    address?: string | null,
    city?: string | null,
    countryName?: string | null,
  ): string {
    const formattedParts = [address, city, countryName].filter(Boolean)
    return formattedParts.join(', ')
  }

  const setResumeDetailsToForm = useCallback(async () => {
    if (completeResume) {
      const formattedAddress = formatFullAddress(
        completeResume?.address,
        completeResume?.city,
        completeResume?.countryName,
      )
      dispatch(
        resumeDistributionActions.setPersonalDetailsForm({
          firstName: completeResume?.firstName,
          lastName: completeResume?.lastName,
          email: completeResume?.email,
          jobTitle: completeResume?.workExperiences?.[0]?.title || '',
          fullAddress: formattedAddress,
        }),
      )

      form.setValue('firstName', completeResume?.firstName || '')
      form.setValue('lastName', completeResume?.lastName || '')
      form.setValue('jobTitle', completeResume?.workExperiences?.[0]?.title || '')
      form.setValue('email', completeResume?.email || '')
      form.setValue('fullAddress', formattedAddress || '')
    } else {
      form.setValue('firstName', '')
      form.setValue('lastName', '')
      form.setValue('jobTitle', '')
      form.setValue('email', '')
      form.setValue('fullAddress', '')
    }
  }, [dispatch, completeResume, form])

  const handleBuilderResumeChange = (data: BriefResume | BriefExpertDoc) => {
    resetFormState()
    setResumeDetailsToForm()

    if (data?.id && data?.type === DocumentTypes.trDocument) {
      parseTrDocuments(data.id)
    } else {
      dispatch(resumeDistributionActions.fetchPopulateFormDetailsForResume({ resumeId: data?.id }))
    }

    setSelectedResumeDetails(data)
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetFormState()
    setResumeDetailsToForm()
    const selectedResume = event.target.files?.[0]

    if (selectedResume) {
      setSelectedResumeDetails(selectedResume)
      // Logic to call parse API for uploaded Resume
      dispatch(resumeDistributionActions.parseResumeForPrefill(selectedResume))
    }
  }

  useEffect(() => {
    resetFormState()
    if (resume) {
      setResumeDetailsToForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setResumeDetailsToForm, resume, dispatch])

  useEffect(() => {
    if (resume && !(resume instanceof File)) {
      dispatch(
        resumeDistributionActions.fetchPopulateFormDetailsForResume({ resumeId: resume?.id }),
      )
    }
  }, [dispatch, resume])

  useEffect(() => {
    const resumes = documentsList.filter(
      doc => doc.type === 'resume' || (doc.type === 'tr_document' && doc.doc_type === 'resume'),
    )

    if (!hasTrackedRef.current && resumes.length > 0) {
      hasTrackedRef.current = true
      trackInternalEvent('see_rd_form_screen', {
        website_host: getHost,
        resumes_count: resumes.length,
      })
    }
  }, [documentsList, getHost])

  return (
    <ResumeDistributionLayout>
      <TitleContainer>
        <Title>{i18n.t(`${TRANSLATION}.title`)}</Title>
      </TitleContainer>
      <Container>
        <HeaderContainer>
          {!resume || resume instanceof File ? (
            <ResumeFilePreview
              editFileName={isEdit && formState?.edit_file_name ? formState?.edit_file_name : null}
              resumePayload={resume}
              isParserLoading={isLoading || isParsing}
              handleFileInputChange={handleFileInputChange}
              handleBuilderResumeChange={handleBuilderResumeChange}
            />
          ) : (
            <ResumeBuilderPreview
              resumePayload={resume}
              isParserLoading={isLoading || isParsing}
              handleFileInputChange={handleFileInputChange}
              handleBuilderResumeChange={handleBuilderResumeChange}
            />
          )}
          <AvailableCountriesBanner />
        </HeaderContainer>
        <FormContainer autoComplete="off">
          <SectionHeader>
            <SectionTitle>{i18n.t(`${TRANSLATION}.personal_info_form.title`)}</SectionTitle>
            <SectionDescription>
              {i18n.t(`${TRANSLATION}.personal_info_form.description`)}
            </SectionDescription>
          </SectionHeader>
          <SimpleForm.Row position="relative">
            {isParsing && (
              <SpinnerRightBar>
                {isParsing ? (
                  <LabelSpinner>
                    <FieldSpinnerLoader />
                  </LabelSpinner>
                ) : (
                  <FullTick />
                )}
                {isParsing ? (
                  <PreFillingText>{i18n.t(`${TRANSLATION}.pre_filling`)}...</PreFillingText>
                ) : (
                  <PreFilledText>{i18n.t(`${TRANSLATION}.pre_filled`)}</PreFilledText>
                )}
              </SpinnerRightBar>
            )}
            <FormGrid $columnGap={16}>
              <FormGridItem $columnSize={isPhone ? 12 : 6}>
                <TextField
                  {...register('firstName', { required })}
                  label={i18n.t(`${TRANSLATION}.personal_info_form.first_name_label`)}
                  placeholder="Herman"
                  error={errors.firstName?.message}
                  autoFocus
                />
              </FormGridItem>
              <FormGridItem $columnSize={isPhone ? 12 : 6}>
                <TextField
                  {...register('lastName', { required })}
                  label={i18n.t(`${TRANSLATION}.personal_info_form.last_name_label`)}
                  placeholder="Walton"
                  error={errors.lastName?.message}
                />
              </FormGridItem>
            </FormGrid>
          </SimpleForm.Row>
          <SimpleForm.Row position="relative">
            <TextField
              {...register('jobTitle', { required })}
              label={i18n.t(`${TRANSLATION}.personal_info_form.job_title_label`)}
              placeholder="Software Engineer"
              error={errors.jobTitle?.message}
            />

            {isParsing && (
              <SpinnerRightBar>
                <FieldSpinnerLoader />
              </SpinnerRightBar>
            )}
          </SimpleForm.Row>
          <SimpleForm.Row position="relative">
            <AsyncAutosuggest
              {...register('fullAddress', { required })}
              name="fullAddress"
              debounceInterval={500}
              fetchItems={async (searchString: string) => {
                const result = await createNewLocationSuggestionsApiFetcher(searchString)
                return result.map(item => ({ text: item.formatted }))
              }}
              onChange={event => {
                setValue('fullAddress', event.target.value, { shouldValidate: true })
              }}
              value={watch('fullAddress')}
              onSuggestionSelected={(_, selected) => {
                setValue('fullAddress', selected?.suggestion?.text || '', { shouldValidate: true })
              }}
            >
              {({ key, ...inputProps }) => (
                <TextField
                  {...inputProps}
                  key={key}
                  label={i18n.t(`${TRANSLATION}.personal_info_form.location_label`)}
                  placeholder={i18n.t(`${TRANSLATION}.personal_info_form.location_placeholder`)}
                  error={errors.fullAddress?.message}
                />
              )}
            </AsyncAutosuggest>
            {!errors.fullAddress?.message && (
              <TextFieldHint>
                {i18n.t(`${TRANSLATION}.personal_info_form.location_description`)}
              </TextFieldHint>
            )}

            {isParsing && (
              <SpinnerRightBar>
                <FieldSpinnerLoader />
              </SpinnerRightBar>
            )}
          </SimpleForm.Row>
          <SimpleForm.Row position="relative">
            <TextField
              {...register('email', { required, validate: validateEmail })}
              label={i18n.t(`${TRANSLATION}.personal_info_form.email_label`)}
              placeholder="herman.walton@gmail.com"
              error={
                errors.email?.message && (
                  <EmailErrorContainer>
                    {errors.email?.message}{' '}
                    {!emailCanBeUsed && (
                      <ContactSupport href="/contact">
                        {i18n.t(`${TRANSLATION}.personal_info_form.email_contact_support`)}
                      </ContactSupport>
                    )}
                  </EmailErrorContainer>
                )
              }
            />

            {isParsing && (
              <SpinnerRightBar>
                <FieldSpinnerLoader />
              </SpinnerRightBar>
            )}
          </SimpleForm.Row>
          <SectionHeader>
            <SectionTitle>{i18n.t(`${TRANSLATION}.job_preferences_form.title`)}</SectionTitle>
            <SectionDescription>
              {i18n.t(`${TRANSLATION}.job_preferences_form.description`)}
            </SectionDescription>
          </SectionHeader>
          <SimpleForm.Row>
            <FormGrid $columnGap={16}>
              <FormGridItem $columnSize={isPhone ? 12 : 6}>
                <Select
                  selected={watch('country') || ''}
                  {...register('country')}
                  onSelect={value => {
                    setValue('country', value?.toString())
                    setFieldValue('country', value?.toString())
                    onChangeLocation('country', value)
                    setValue('state', undefined)
                    setFieldValue('state', '')
                    onChangeLocation('state', '')
                    trackInternalEvent('select_job_country', {
                      label: 'rd_form',
                      website_host: getHost,
                    })
                  }}
                  options={COUNTRIES}
                  label={i18n.t(`${TRANSLATION}.job_preferences_form.country_label`)}
                />
              </FormGridItem>
              <FormGridItem $columnSize={isPhone ? 12 : 6}>
                <AsyncAutosuggest<TransformedItem>
                  highlightedQuery
                  value={location.state}
                  onChange={e => onChangeLocation('state', e.target.value)}
                  onSuggestionSelected={onSuggestionSelected('state')}
                  fetchItems={query =>
                    fetchStatesSuggestions(getValues('country') || undefined)(query)
                  }
                >
                  {({ key, ...inputProps }) => (
                    <TextField
                      autoComplete="off"
                      role="presentation"
                      {...register('state', { required })}
                      {...inputProps}
                      key={key}
                      error={getError('state')}
                      name="state"
                      label={i18n.t(`${TRANSLATION}.job_preferences_form.state_label`)}
                      placeholder="E.g. California (CA)"
                    />
                  )}
                </AsyncAutosuggest>
              </FormGridItem>
            </FormGrid>
          </SimpleForm.Row>
          <SimpleForm.Row>
            <Toggle
              checked={Boolean(watch('remote'))}
              {...register('remote')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onSelectField('remote', e.target.checked.toString())
                if (e.target.checked) {
                  trackInternalEvent('rd_open_to_remote', {
                    label: 'total_job_search_solution',
                  })
                }
              }}
              label={
                <ToggleLabel>
                  {i18n.t(`${TRANSLATION}.job_preferences_form.remote_label`)}
                </ToggleLabel>
              }
            />
          </SimpleForm.Row>
          <SimpleForm.Row>
            <FieldNote
              error={
                getError('industry') && i18n.t(`${TRANSLATION}.job_preferences_form.industry_label`)
              }
              description={
                !getError('industry') &&
                i18n.t(`${TRANSLATION}.job_preferences_form.industry_label`)
              }
            />
            <StyledSelect
              isSearchable
              isClearable
              {...register('industry', { required })}
              name="skills"
              value={selectedIndustry}
              classNamePrefix="select"
              options={industries}
              placeholder={i18n.t(`${TRANSLATION}.job_preferences_form.industry_placeholder`)}
              onChange={option => {
                const value = option?.value || ''
                onSelectField('industry', value)
                setValue('industry', value, { shouldValidate: true })
                trackInternalEvent('select_industry', {
                  label: 'rd_form',
                  industry: option?.label,
                  website_host: getHost,
                })
              }}
            />
            <FieldNote error={getError('industry')} />
          </SimpleForm.Row>
          <SimpleForm.Row>
            <FormGrid $columnGap={8}>
              <FormGridItem $columnSize={3}>
                <Select
                  selected={watch('salaryCurrency')}
                  {...register('salaryCurrency')}
                  onSelect={value => onSelectField('salaryCurrency', value)}
                  options={CURRENCY}
                />
              </FormGridItem>
              <FormGridItem $columnSize={5}>
                <TextFieldAmount
                  inputIcon={CURRENCY_SIGN[getValues('salaryCurrency')]}
                  value={watch('salaryAmount') || ''}
                  {...register('salaryAmount', {
                    valueAsNumber: true,
                    required,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      debouncedTrackFieldChange('salaryAmount', e.target.value)
                    },
                    validate: val => (isNaN(Number(val)) ? 'The amount should be number' : true),
                  })}
                  error={getError('salaryAmount')}
                  placeholder="100,000"
                />
              </FormGridItem>
              <FormGridItem $columnSize={4}>
                <Select
                  selected={watch('salaryPeriod')}
                  {...register('salaryPeriod')}
                  onSelect={value => onSelectField('salaryPeriod', value)}
                  options={PERIOD}
                />
              </FormGridItem>
            </FormGrid>
          </SimpleForm.Row>
          <RecruiterCountBanner data={form.getValues()} />
          <ActionContainer>
            <ButtonV2
              isLoading={isEmailValidationPending}
              onClick={() => {
                trackInternalEvent('click_start_distributing', {
                  label: 'rd_form',
                  website_host: getHost,
                })
                form.handleSubmit(handleSubmit)()
              }}
            >
              {i18n.t(`${TRANSLATION}.continue_button_text`)}
            </ButtonV2>
          </ActionContainer>
        </FormContainer>
      </Container>
    </ResumeDistributionLayout>
  )
}
